import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, User, Award, ClipboardList } from 'lucide-react'
import DemoRoleSelectionModal from './DemoRoleSelectionModal'

interface DemoModalProps {
  isOpen: boolean
  onClose: () => void
}

const DemoModal = ({ isOpen, onClose }: DemoModalProps) => {
  const navigate = useNavigate()
  const [isRoleSelectionOpen, setIsRoleSelectionOpen] = useState(false)
  const [selectedScenario, setSelectedScenario] = useState<string>('')

  const handleScenarioSelect = (scenario: string) => {
    setSelectedScenario(scenario)
    setIsRoleSelectionOpen(true)
  }

  const handleDemoStart = (role: string) => {
    // デモユーザーとしてログイン処理（実際の実装では適切な認証処理を行う）
    console.log('Starting demo with scenario:', selectedScenario, 'role:', role)
    
    // デモ用のセッションを作成してダッシュボードへ
    navigate('/evaluation')
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full">
          <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">デモ体験を開始</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6">
              体験したいシナリオを選択してください。
              実際の評価業務の流れをご体験いただけます。
            </p>

            <div className="space-y-4">
              <button
                onClick={() => handleScenarioSelect('student_report')}
                className="w-full text-left p-6 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <User className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      学生レポート評価
                    </h3>
                    <p className="text-sm text-gray-600">
                      30名分の学生レポートを、複数の観点から評価します。
                      教育現場での実際の使用例を体験できます。
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleScenarioSelect('job_interview')}
                className="w-full text-left p-6 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <ClipboardList className="w-6 h-6 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      採用面接評価
                    </h3>
                    <p className="text-sm text-gray-600">
                      候補者の面接評価を行います。
                      人事部門での活用例を体験できます。
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleScenarioSelect('contest_judging')}
                className="w-full text-left p-6 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <Award className="w-6 h-6 text-purple-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      コンテスト審査
                    </h3>
                    <p className="text-sm text-gray-600">
                      作品コンテストの審査を行います。
                      複数審査員での評価例を体験できます。
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                ※ デモ環境では仮想的なデータを使用します
                <br />
                ※ デモ中に作成したデータは保存されません
              </p>
            </div>
          </div>
        </div>
      </div>

      <DemoRoleSelectionModal
        isOpen={isRoleSelectionOpen}
        onClose={() => setIsRoleSelectionOpen(false)}
        scenario={selectedScenario}
        onSelectRole={handleDemoStart}
      />
    </>
  )
}

export default DemoModal